/**
 * In-thread destructive-approval affordance for Shogun `platform_call`
 * (DESKTOP arm). On desktop the daemon gates a
 * destructive call server-side and reports it back as `confirmation_required`;
 * this card surfaces the pending change and lets the user Approve (POST the
 * server-stamped idempotency key back to the daemon so the re-issued call
 * executes) or Dismiss (do nothing).
 *
 * PROPS-DRIVEN — it fetches nothing. The host (`ChatView`) owns the SDK call,
 * the `isApproving` flag, and any error string; this component only renders.
 * The preview carries no secret args (only {resource, method}), so nothing is
 * masked or logged here.
 */

import type { ExperienceMode, PlatformApprovalRequest } from '@localagent/sdk';
import { NO_NAMED_TARGET } from '../chat/named-target';

interface ShogunApprovalCardProps {
  request: PlatformApprovalRequest;
  onApprove: () => void | Promise<void>;
  onDismiss: () => void;
  isApproving?: boolean;
  /** Error surfaced from a failed Approve; never swallowed. */
  error?: string | null;
  mode?: ExperienceMode;
}

export function ShogunApprovalCard({
  request,
  onApprove,
  onDismiss,
  isApproving,
  error,
  mode,
}: ShogunApprovalCardProps) {
  const isNormal = mode !== 'advanced';
  return (
    <div
      data-testid="shogun-approval-card"
      className="rounded bg-amber-500/10 p-2.5"
    >
      {isNormal ? (
        <p className="mb-1 text-xs font-medium text-foreground">
          Shogun wants to continue.{' '}
          <span data-testid="shogun-approval-no-target">{NO_NAMED_TARGET}</span>
        </p>
      ) : (
        <p className="mb-1 text-xs font-medium text-foreground">
          Shogun wants to run{' '}
          <code
            data-testid="shogun-approval-action"
            className="rounded bg-muted px-1 py-0.5 text-[11px]"
          >
            {request.resource}.{request.method}
          </code>
        </p>
      )}
      <p className="mb-2 text-[11px] text-amber-700 dark:text-amber-400">
        This will make a permanent change that is not automatically reversible.
      </p>

      {error && (
        <p data-testid="shogun-approval-error" className="mb-2 text-[11px] text-destructive">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="shogun-approval-approve"
          disabled={isApproving}
          className="rounded bg-amber-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onApprove();
          }}
        >
          {isApproving ? 'Approving…' : 'Approve'}
        </button>
        <button
          type="button"
          data-testid="shogun-approval-dismiss"
          disabled={isApproving}
          className="rounded px-3 py-1 text-[11px] text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
